import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderRuleSetSchema, type OrderCondition, type OrderRuleSet } from "@logiful/domain";
import { ArrowDown, ArrowUp, Ban, Clock, Eye, Gift, History, PackageX, PauseCircle, Plus, RotateCcw, Trash2, Truck, Workflow, Zap } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { ShippingMethodSelect } from "@/components/ShippingMethodSelect";
import { BackorderSettingsCard, PartialLimitsEditor } from "@/components/BackorderSettings";
import { ExportDocumentSettingsCard } from "@/components/ExportDocument";
import { ReservationSettingsCard } from "@/components/ReservationSettings";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, type RulesPreview } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { serverText } from "@/lib/server-text";
import { intlLocale, msg, t } from "@/lib/i18n";

type Rule = OrderRuleSet["rules"][number];
type Action = Rule["then"][number];
type Field = OrderCondition["field"];

/** Bedingungen: Label, Operatoren und Werttyp – neue Felder nur hier ergänzen */
const FIELDS: Record<Field, { label: string; ops: { op: string; label: string }[]; kind: "list" | "text" | "number"; placeholder: string; unit?: string }> = {
  country: { label: msg("Lieferland"), ops: [{ op: "in", label: msg("ist eines von") }, { op: "not_in", label: msg("ist keines von") }], kind: "list", placeholder: msg("DE, AT, CH") },
  shippingMethod: { label: msg("Versandart im Shop"), ops: [{ op: "contains", label: msg("enthält") }, { op: "equals", label: msg("ist genau") }], kind: "text", placeholder: msg("Express") },
  tag: { label: msg("Tag der Bestellung"), ops: [{ op: "has", label: msg("ist gesetzt") }, { op: "has_not", label: msg("ist nicht gesetzt") }], kind: "text", placeholder: msg("vip") },
  financialStatus: { label: msg("Zahlstatus"), ops: [{ op: "in", label: msg("ist eines von") }, { op: "not_in", label: msg("ist keines von") }], kind: "list", placeholder: msg("paid, authorized") },
  orderTotal: { label: msg("Warenwert"), ops: [{ op: "gte", label: msg("mindestens") }, { op: "lte", label: msg("höchstens") }], kind: "number", placeholder: "100", unit: "€" },
  itemCount: { label: msg("Stückzahl"), ops: [{ op: "gte", label: msg("mindestens") }, { op: "lte", label: msg("höchstens") }], kind: "number", placeholder: "10", unit: "Stk." },
  sku: { label: msg("Artikel (SKU)"), ops: [{ op: "contains_any", label: msg("enthält einen von") }, { op: "contains_none", label: msg("enthält keinen von") }], kind: "list", placeholder: msg("SKU-1, SKU-2") },
  noteContains: { label: msg("Notiz"), ops: [{ op: "contains", label: msg("enthält") }], kind: "text", placeholder: msg("Geschenk") },
};

const ACTIONS: Record<Action["type"], { label: string; icon: ReactNode; field: "reason" | "shippingMethodId" | "priority" | "minutes" | "sku" | "mode"; placeholder: string; unit?: string }> = {
  hold: { label: msg("Zurückhalten mit Grund"), icon: <PauseCircle className="size-3.5 text-warning" />, field: "reason", placeholder: msg("Zollrelevante Daten prüfen") },
  reject: { label: msg("Ablehnen mit Grund"), icon: <Ban className="size-3.5 text-danger" />, field: "reason", placeholder: msg("Testauftrag") },
  setShippingMethod: { label: msg("Versandart im Lager"), icon: <Truck className="size-3.5 text-fg-muted" />, field: "shippingMethodId", placeholder: msg("DHL-EXPRESS") },
  setPriority: { label: msg("Priorität (−5 bis 5)"), icon: <Zap className="size-3.5 text-coral" />, field: "priority", placeholder: "5" },
  delay: { label: msg("Später übergeben"), icon: <Clock className="size-3.5 text-fg-muted" />, field: "minutes", placeholder: "60", unit: "Min." },
  addLine: { label: msg("Position hinzufügen (Beilage)"), icon: <Gift className="size-3.5 text-coral" />, field: "sku", placeholder: msg("FLYER-HERBST") },
  backorder: { label: msg("Bei Fehlbestand"), icon: <PackageX className="size-3.5 text-fg-muted" />, field: "mode", placeholder: "" },
};

type BackorderAction = Extract<Action, { type: "backorder" }>;
/** Auswahl wie in „Aufträge mit Fehlbestand“ – Art + Übergabe in einem Feld */
const BACKORDER_CHOICES: { value: string; label: string; set: Pick<BackorderAction, "mode" | "handover"> }[] = [
  { value: "partial", label: msg("teilliefern"), set: { mode: "partial", handover: "fulfiller" } },
  { value: "hold", label: msg("im Portal zurückhalten"), set: { mode: "hold", handover: "fulfiller" } },
  { value: "transfer-fulfiller", label: msg("trotzdem direkt an Logiful"), set: { mode: "transfer", handover: "fulfiller" } },
  { value: "transfer-merchant", label: msg("nur ins FFN, an Logiful sobald Bestand da"), set: { mode: "transfer", handover: "merchant" } },
];
const backorderChoice = (a: BackorderAction) => (a.mode === "transfer" ? `transfer-${a.handover}` : a.mode);

const newCondition = (field: Field = "country"): OrderCondition => {
  const f = FIELDS[field];
  const op = f.ops[0]!.op;
  return { field, op, value: f.kind === "list" ? [] : f.kind === "number" ? 0 : "" } as unknown as OrderCondition;
};
const newAction = (type: Action["type"] = "hold"): Action =>
  (type === "hold" || type === "reject" ? { type, reason: "" } : type === "setShippingMethod" ? { type, shippingMethodId: "" } : type === "setPriority" ? { type, priority: 5 } : type === "addLine" ? { type, sku: "", mode: "fixed", factor: 1, countSkus: [], required: false } : type === "backorder" ? { type, mode: "partial", handover: "fulfiller", minSharePercent: 50, maxParts: 2 } : { type, minutes: 60 }) as Action;

type AddLine = Extract<Action, { type: "addLine" }>;
const MODES: { value: AddLine["mode"]; label: string }[] = [
  { value: "fixed", label: msg("feste Menge") },
  { value: "perItem", label: msg("je Artikel im Auftrag") },
  { value: "perValue", label: msg("je Warenwert") },
];
/** Lesbare Zusammenfassung, z. B. "1 Stk. je 2 Artikel, höchstens 3" */
function addLineSummary(a: AddLine) {
  const f = a.factor.toLocaleString(intlLocale());
  const base = a.mode === "fixed" ? `${f} Stk.` : a.mode === "perItem" ? (a.factor < 1 ? `1 Stk. je ${(1 / a.factor).toLocaleString(intlLocale(), { maximumFractionDigits: 2 })} Artikel` : `${f} Stk. je Artikel`) + (a.countSkus.length ? ` (nur ${a.countSkus.join(", ")})` : "") : `${f} Stk. je ${(a.stepEur ?? 0).toLocaleString(intlLocale())} € Warenwert`;
  return `${base}, abgerundet${a.maxQuantity ? `, höchstens ${a.maxQuantity}` : ""}${a.required ? " · Pflicht" : " · optional"}`;
}

function AddLineEditor({ a, onChange }: { a: AddLine; onChange: (a: AddLine) => void }) {
  const sel = "h-7 rounded-md border border-border bg-surface px-1.5 text-[12.5px]";
  return (
    <div className="w-full mt-1.5 pl-5 space-y-2">
      <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-fg-muted">
        <span>{t("Menge")}</span>
        <select value={a.mode} onChange={(e) => onChange({ ...a, mode: e.target.value as AddLine["mode"], ...(e.target.value === "perValue" && !a.stepEur ? { stepEur: 50 } : {}) })} className={sel} aria-label={t("Mengenberechnung")}>
          {MODES.map((m) => <option key={m.value} value={m.value}>{t(m.label)}</option>)}
        </select>
        <span>{t("Faktor")}</span>
        <Input type="number" step="0.1" min={0.01} value={String(a.factor)} onChange={(e) => onChange({ ...a, factor: Number(e.target.value) || 1 })} className="h-7 w-20 tabular-nums" aria-label={t("Faktor")} />
        {a.mode === "perValue" && (<><span>{t("je")}</span><Input type="number" min={1} value={String(a.stepEur ?? 50)} onChange={(e) => onChange({ ...a, stepEur: Number(e.target.value) || 1 })} suffix="€" className="h-7 w-24 tabular-nums" aria-label={t("Warenwert-Schritt")} /></>)}
        <span>{t("höchstens")}</span>
        <Input type="number" min={1} value={a.maxQuantity ? String(a.maxQuantity) : ""} onChange={(e) => { const n = Math.round(Number(e.target.value)); const { maxQuantity: _m, ...rest } = a; onChange(n > 0 ? { ...rest, maxQuantity: n } : rest); }} placeholder="∞" className="h-7 w-16 tabular-nums" aria-label={t("Höchstmenge")} />
      </div>
      {a.mode === "perItem" && (
        <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-fg-muted">
          <span>{t("nur diese Artikel zählen")}</span>
          <ListOrValueInput initial={a.countSkus.join(", ")} placeholder={t("leer = alle Artikel")} onCommit={(v) => onChange({ ...a, countSkus: v.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean) })} />
        </div>
      )}
      <label className="flex items-start gap-2 text-[12.5px] cursor-pointer">
        <input type="checkbox" checked={a.required} onChange={(e) => onChange({ ...a, required: e.target.checked })} className="mt-0.5 accent-ink" />
        <span><span className="font-medium text-fg">{t("Pflicht")}</span><span className="text-fg-muted"> {t("– ohne verfügbaren Bestand wird der Auftrag zurückgehalten („Beilage out of stock“) und automatisch freigegeben, sobald wieder Bestand da ist. Sonst entfällt die Beilage einfach.")}</span></span>
      </label>
      <div className="text-[12px] text-fg-subtle">{addLineSummary(a)}</div>
    </div>
  );
}

export function RulesPage() {
  const { tenantId } = useTenant();
  const channels = useQuery({ queryKey: ["channels", tenantId], queryFn: () => api.channels.list(tenantId) });
  const [channelId, setChannelId] = useState<string | null>(null);
  const cid = channelId ?? channels.data?.[0]?.id ?? null;

  return (
    <div className="pb-24">
      <PageHeader
        title={t("Auftragsregeln")}
        description={t("Legt fest, was mit Aufträgen passiert, bevor sie ins Lager gehen – je Shop, versioniert, mit Vorschau.")}
        actions={
          channels.data && channels.data.length > 1 ? (
            <select value={cid ?? ""} onChange={(e) => setChannelId(e.target.value)} className="h-8 rounded-lg border border-border bg-surface px-2.5 text-[13px]">
              {channels.data.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          ) : undefined
        }
      />
      <div className="px-4 md:px-8">
        <ReservationSettingsCard />
        <BackorderSettingsCard />
        <ExportDocumentSettingsCard />
        {channels.isLoading && <Skeleton className="h-40 w-full" />}
        {channels.data?.length === 0 && <Card><EmptyState icon={<Workflow className="size-5" />} title={t("Noch kein Shop verbunden")}>{t("Regeln gelten je Shop. Verbinde zuerst einen Shop.")}</EmptyState></Card>}
        {cid && <RulesEditor key={cid} channelId={cid} />}
      </div>
    </div>
  );
}

function RulesEditor({ channelId }: { channelId: string }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["rules", tenantId, channelId], queryFn: () => api.rules.get(tenantId, channelId) });
  const [draft, setDraft] = useState<OrderRuleSet | null>(null);
  const [preview, setPreview] = useState<RulesPreview | null>(null);

  useEffect(() => {
    if (q.data && !draft) setDraft(structuredClone(q.data.active.definition));
  }, [q.data, draft]);

  const dirty = useMemo(() => !!q.data && !!draft && JSON.stringify(q.data.active.definition) !== JSON.stringify(draft), [q.data, draft]);
  const parsed = draft ? orderRuleSetSchema.safeParse(draft) : null;
  const errors = parsed && !parsed.success ? parsed.error.issues : [];
  const errorAt = (path: (string | number)[]) => errors.some((e) => path.every((p, i) => e.path[i] === p));

  const previewMut = useMutation({ mutationFn: () => api.rules.preview(tenantId, channelId, draft!), onSuccess: setPreview, onError: () => toast.error("Vorschau fehlgeschlagen") });
  const saveMut = useMutation({
    mutationFn: () => api.rules.save(tenantId, channelId, draft!),
    onSuccess: (r) => {
      toast.success(`Version ${r.version} ist aktiv`, { description: msg("Gilt ab sofort für neue Aufträge.") });
      setPreview(null);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["rules", tenantId, channelId] });
    },
  });
  const restoreMut = useMutation({
    mutationFn: (v: number) => api.rules.activate(tenantId, channelId, v),
    onSuccess: (r) => {
      toast.success(`Version ${r.version} wieder aktiv`);
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["rules", tenantId, channelId] });
    },
  });

  if (!q.data || !draft) return <Skeleton className="h-64 w-full" />;
  const update = (fn: (d: OrderRuleSet) => void) => setDraft((d) => { const c = structuredClone(d!); fn(c); return c; });

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6">
      <div className="space-y-4 min-w-0">
        {/* Grundeinstellungen */}
        <Card className="p-5">
          <h2 className="font-semibold">{t("Grundeinstellungen")}</h2>
          <p className="text-fg-muted text-[12.5px] mt-0.5">{t("Gelten für jeden Auftrag dieses Shops.")}</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted">{t("Stornofenster")}</span>
              <Input type="number" min={0} value={String(draft.delayMinutes)} onChange={(e) => update((d) => void (d.delayMinutes = Math.max(0, Math.round(Number(e.target.value) || 0))))} suffix={t("Min.")} className="mt-1" />
              <span className="text-[11.5px] text-fg-subtle">{t("Wartezeit, in der Kunden noch stornieren können")}</span>
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted">{t("Standard-Versandart")}</span>
              <ShippingMethodSelect value={draft.defaultShippingMethodId} onChange={(id) => update((d) => void (d.defaultShippingMethodId = id))} emptyLabel={t("keine – Versandarten-Zuordnung / Lager-Standard")} className="mt-1" ariaLabel={t("Standard-Versandart")} />
              <span className="text-[11.5px] text-fg-subtle">{t("Wenn keine Regel eine Versandart setzt")}</span>
            </label>
            <div>
              <span className="text-[12px] font-medium text-fg-muted">{t("Zahlung")}</span>
              <button role="switch" aria-checked={draft.requirePaid} onClick={() => update((d) => void (d.requirePaid = !d.requirePaid))} className="mt-1 flex items-center gap-2.5 h-8">
                <span className={cx("w-8 h-[18px] rounded-full p-0.5 transition-colors", draft.requirePaid ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", draft.requirePaid && "translate-x-3.5")} /></span>
                <span className="text-[13px]">{t("Nur bezahlte Aufträge versenden")}</span>
              </button>
            </div>
          </div>
        </Card>

        {/* Regeln */}
        {draft.rules.map((r, ri) => (
          <Card key={r.id} className={cx("p-5", !r.enabled && "opacity-60")}>
            <div className="flex items-center gap-2">
              <span className="size-6 rounded-md bg-surface-2 border border-border grid place-items-center text-[11.5px] font-semibold tabular-nums">{ri + 1}</span>
              <Input value={r.name} onChange={(e) => update((d) => void (d.rules[ri]!.name = e.target.value))} invalid={errorAt(["rules", ri, "name"])} className="flex-1 font-medium" aria-label={t("Regelname")} />
              <button title={t("Nach oben")} disabled={ri === 0} onClick={() => update((d) => void d.rules.splice(ri - 1, 0, d.rules.splice(ri, 1)[0]!))} className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted disabled:opacity-30"><ArrowUp className="size-4" /></button>
              <button title={t("Nach unten")} disabled={ri === draft.rules.length - 1} onClick={() => update((d) => void d.rules.splice(ri + 1, 0, d.rules.splice(ri, 1)[0]!))} className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted disabled:opacity-30"><ArrowDown className="size-4" /></button>
              <button role="switch" aria-checked={r.enabled} title={r.enabled ? t("Regel deaktivieren") : t("Regel aktivieren")} onClick={() => update((d) => void (d.rules[ri]!.enabled = !d.rules[ri]!.enabled))} className="px-1">
                <span className={cx("block w-8 h-[18px] rounded-full p-0.5 transition-colors", r.enabled ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", r.enabled && "translate-x-3.5")} /></span>
              </button>
              <button title={t("Regel löschen")} onClick={() => update((d) => void d.rules.splice(ri, 1))} className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-subtle hover:text-danger"><Trash2 className="size-4" /></button>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted mb-2">{t("Wenn")} {r.when.length > 1 ? t("alle zutreffen") : ""}</p>
                <div className="space-y-2">
                  {r.when.length === 0 && <p className="text-[12.5px] text-fg-subtle">{t("Immer (für jeden Auftrag)")}</p>}
                  {r.when.map((c, ci) => (
                    <ConditionRow key={ci} c={c} invalid={errorAt(["rules", ri, "when", ci])} onChange={(nc) => update((d) => void (d.rules[ri]!.when[ci] = nc))} onRemove={() => update((d) => void d.rules[ri]!.when.splice(ci, 1))} />
                  ))}
                  <button onClick={() => update((d) => void d.rules[ri]!.when.push(newCondition()))} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-fg-muted hover:text-fg"><Plus className="size-3.5" /> {t("Bedingung")}</button>
                </div>
              </div>
              <div>
                <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted mb-2">{t("Dann")}</p>
                <div className="space-y-2">
                  {r.then.map((a, ai) => (
                    <ActionRow key={ai} a={a} invalid={errorAt(["rules", ri, "then", ai])} onChange={(na) => update((d) => void (d.rules[ri]!.then[ai] = na))} onRemove={r.then.length > 1 ? () => update((d) => void d.rules[ri]!.then.splice(ai, 1)) : undefined} />
                  ))}
                  <button onClick={() => update((d) => void d.rules[ri]!.then.push(newAction("setPriority")))} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-fg-muted hover:text-fg"><Plus className="size-3.5" /> {t("Aktion")}</button>
                </div>
              </div>
            </div>
          </Card>
        ))}

        <button
          onClick={() => update((d) => void d.rules.push({ id: `r${Date.now().toString(36)}`, name: "Neue Regel", enabled: true, when: [newCondition()], then: [newAction()] }))}
          className="w-full h-12 rounded-xl border border-dashed border-border-strong text-fg-muted hover:text-fg hover:bg-surface-2/50 inline-flex items-center justify-center gap-1.5 font-medium"
        >
          <Plus className="size-4" /> {t("Regel hinzufügen")}
        </button>

        {preview && <PreviewPanel p={preview} />}
      </div>

      {/* Seitenleiste: Versionen */}
      <aside className="space-y-4">
        <Card className="p-4">
          <h3 className="font-semibold flex items-center gap-2"><History className="size-4 text-fg-subtle" /> {t("Versionen")}</h3>
          <ul className="mt-3 space-y-1">
            {q.data.versions.length === 0 && <li className="text-[12.5px] text-fg-muted">{t("Standardregeln aktiv – noch keine eigene Version.")}</li>}
            {q.data.versions.map((v) => (
              <li key={v.version} className="flex items-center gap-2 h-8">
                <span className="font-medium tabular-nums text-[13px]">v{v.version}</span>
                <span className="text-[12px] text-fg-subtle">{fmtDateTime(v.createdAt)}</span>
                {v.isActive ? <Badge tone="success" className="ml-auto">{t("aktiv")}</Badge> : (
                  <button onClick={() => restoreMut.mutate(v.version)} className="ml-auto inline-flex items-center gap-1 text-[12px] text-fg-muted hover:text-fg"><RotateCcw className="size-3" /> {t("aktivieren")}</button>
                )}
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-4 text-[12.5px] text-fg-muted space-y-1.5">
          <p className="font-medium text-fg">{t("So werden Regeln angewendet")}</p>
          <p>{t("1. Eingebaute Prüfungen: Zahlung, Adresse, Stammdaten.")}</p>
          <p>{t("2. Deine Regeln von oben nach unten. Gründe zum Zurückhalten sammeln sich, Ablehnen hat Vorrang.")}</p>
          <p>{t("3. Versandart und Priorität: Die erste passende Regel gewinnt.")}</p>
          <p>{t("4. „Bei Fehlbestand“: Die erste passende Regel gewinnt vor der Einstellung „Aufträge mit Fehlbestand“.")}</p>
        </Card>
      </aside>

      {/* Aktionsleiste */}
      {(dirty || preview) && (
        <div className="fixed bottom-6 left-1/2 md:left-[calc(50%+120px)] -translate-x-1/2 z-30 flex items-center gap-2 pl-4 pr-2 h-12 max-w-[calc(100vw-32px)] whitespace-nowrap rounded-xl bg-ink text-ink-fg shadow-2xl animate-pop-in">
          <span className="text-[13px]">{errors.length ? t("{length} Feld{v} unvollständig", { length: errors.length, v: errors.length > 1 ? t("er") : "" }) : dirty ? t("Ungespeicherte Änderungen") : t("Vorschau aktuell")}</span>
          <span className="w-px h-5 bg-ink-fg/20 mx-1" />
          {dirty && <button onClick={() => { setDraft(structuredClone(q.data.active.definition)); setPreview(null); }} className="h-8 px-3 rounded-lg hover:bg-ink-fg/10 text-[13px]">{t("Verwerfen")}</button>}
          <button disabled={!!errors.length || previewMut.isPending} onClick={() => previewMut.mutate()} className="h-8 px-3 rounded-lg hover:bg-ink-fg/10 text-[13px] font-medium inline-flex items-center gap-1.5 disabled:opacity-40"><Eye className="size-3.5" /> {t("Vorschau")}</button>
          {dirty && <button disabled={!!errors.length || saveMut.isPending} onClick={() => saveMut.mutate()} className="h-8 px-3 rounded-lg bg-ink-fg text-ink text-[13px] font-semibold disabled:opacity-40">{t("Speichern & aktivieren")}</button>}
        </div>
      )}
    </div>
  );
}

function ConditionRow({ c, onChange, onRemove, invalid }: { c: OrderCondition; onChange: (c: OrderCondition) => void; onRemove: () => void; invalid: boolean }) {
  const f = FIELDS[c.field];
  const value = Array.isArray(c.value) ? c.value.join(", ") : String(c.value);
  const setValue = (raw: string) => {
    const v = f.kind === "list" ? raw.split(",").map((s) => (c.field === "country" ? s.trim().toUpperCase() : s.trim())).filter(Boolean) : f.kind === "number" ? Number(raw.replace(",", ".")) || 0 : raw;
    onChange({ ...c, value: v } as OrderCondition);
  };
  return (
    <div className={cx("flex flex-wrap items-center gap-1.5 p-2 rounded-lg border", invalid ? "border-danger/40 bg-danger-soft/40" : "border-border bg-surface-2/40")}>
      <select value={c.field} onChange={(e) => onChange(newCondition(e.target.value as Field))} className="h-7 rounded-md border border-border bg-surface px-1.5 text-[12.5px]" aria-label={t("Feld")}>
        {Object.entries(FIELDS).map(([k, v]) => <option key={k} value={k}>{t(v.label)}</option>)}
      </select>
      <select value={c.op} onChange={(e) => onChange({ ...c, op: e.target.value } as OrderCondition)} className="h-7 rounded-md border border-border bg-surface px-1.5 text-[12.5px]" aria-label={t("Vergleich")}>
        {f.ops.map((o) => <option key={o.op} value={o.op}>{t(o.label)}</option>)}
      </select>
      <ListOrValueInput key={c.field} initial={value} placeholder={t(f.placeholder)} unit={f.unit} numeric={f.kind === "number"} onCommit={setValue} />
      <button onClick={onRemove} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label={t("Bedingung entfernen")}><Trash2 className="size-3.5" /></button>
    </div>
  );
}

/** Eingabe mit lokalem Entwurf: Listen werden erst beim Verlassen geparst, damit Kommas tippbar bleiben */
function ListOrValueInput({ initial, placeholder, unit, numeric, onCommit }: { initial: string; placeholder: string; unit?: string; numeric?: boolean; onCommit: (v: string) => void }) {
  const [v, setV] = useState(initial === "0" ? "" : initial);
  return <Input value={v} onChange={(e) => { setV(e.target.value); if (numeric) onCommit(e.target.value); }} onBlur={() => onCommit(v)} placeholder={placeholder} suffix={unit} inputMode={numeric ? "decimal" : undefined} className="h-7 flex-1 min-w-[120px]" />;
}

function ActionRow({ a, onChange, onRemove, invalid }: { a: Action; onChange: (a: Action) => void; onRemove?: () => void; invalid: boolean }) {
  const meta = ACTIONS[a.type];
  const raw = (a as Record<string, unknown>)[meta.field];
  return (
    <div className={cx("flex flex-wrap items-center gap-1.5 p-2 rounded-lg border", invalid ? "border-danger/40 bg-danger-soft/40" : "border-border bg-surface-2/40")}>
      {meta.icon}
      <select value={a.type} onChange={(e) => onChange(newAction(e.target.value as Action["type"]))} className="h-7 rounded-md border border-border bg-surface px-1.5 text-[12.5px]" aria-label={t("Aktion")}>
        {Object.entries(ACTIONS).map(([k, v]) => <option key={k} value={k}>{t(v.label)}</option>)}
      </select>
      {a.type === "backorder" ? (
        <select value={backorderChoice(a)} onChange={(e) => { const c = BACKORDER_CHOICES.find((x) => x.value === e.target.value)!; onChange({ ...a, ...c.set }); }} className="h-7 flex-1 min-w-[160px] rounded-md border border-border bg-surface px-1.5 text-[12.5px]" aria-label={t("Verhalten bei Fehlbestand")}>
          {BACKORDER_CHOICES.map((c) => <option key={c.value} value={c.value}>{t(c.label)}</option>)}
        </select>
      ) : meta.field === "shippingMethodId" ? (
        <ShippingMethodSelect value={String(raw ?? "")} onChange={(id) => onChange({ ...a, shippingMethodId: id ?? "" } as Action)} className="h-7 flex-1 min-w-[160px] text-[12.5px]" ariaLabel={t("Versandart im Lager")} />
      ) : (
      <Input
        value={String(raw ?? "")}
        onChange={(e) => {
          const val = meta.field === "priority" || meta.field === "minutes" ? Math.round(Number(e.target.value) || 0) : e.target.value;
          onChange({ ...a, [meta.field]: val } as Action);
        }}
        placeholder={t(meta.placeholder)}
        suffix={meta.unit}
        className={cx("h-7 flex-1 min-w-[120px]", meta.field === "sku" && "font-mono")}
      />
      )}
      {onRemove && <button onClick={onRemove} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label={t("Aktion entfernen")}><Trash2 className="size-3.5" /></button>}
      {a.type === "addLine" && <AddLineEditor a={a} onChange={onChange} />}
      {a.type === "backorder" && a.mode === "partial" && (
        <div className="w-full mt-1.5 pl-5"><PartialLimitsEditor value={a} onChange={(l) => onChange({ ...a, ...l })} /></div>
      )}
    </div>
  );
}

function PreviewPanel({ p }: { p: RulesPreview }) {
  const [filter, setFilter] = useState<"all" | "release" | "hold" | "reject">("all");
  const list = p.orders.filter((o) => filter === "all" || o.decision === filter);
  const toggle = (k: "release" | "hold" | "reject") => setFilter((f) => (f === k ? "all" : k));
  return (
    <Card className="p-5 animate-fade-in">
      <h2 className="font-semibold flex items-center gap-2"><Eye className="size-4 text-fg-subtle" /> {t("Vorschau auf die letzten")} {p.total} {t("Aufträge")}</h2>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {([["release", msg("Freigegeben"), p.released, "text-success"], ["hold", msg("Zurückgehalten"), p.held, "text-warning"], ["reject", msg("Abgelehnt"), p.rejected, "text-danger"]] as const).map(([k, label, n, color]) => (
          <button key={k} onClick={() => toggle(k)} aria-pressed={filter === k} className={cx("rounded-lg border p-3 text-left", filter === k ? "border-ink" : "border-border hover:bg-surface-2/50")}>
            <div className={cx("text-[22px] font-semibold tabular-nums", color)}>{n}</div>
            <div className="text-[12px] text-fg-muted">{t(label)}</div>
          </button>
        ))}
      </div>
      <ul className="mt-3 divide-y divide-border max-h-72 overflow-y-auto">
        {list.map((o) => (
          <li key={o.orderId} className="flex items-center gap-3 py-2 text-[13px]">
            <span className="font-medium tabular-nums w-16">{o.orderNumber}</span>
            {o.decision === "release" ? <Badge tone="success">{t("frei")}</Badge> : o.decision === "hold" ? <Badge tone="warning">{t("zurückgehalten")}</Badge> : <Badge tone="danger">{t("abgelehnt")}</Badge>}
            <span className="text-fg-muted truncate">{o.rejectReason ?? o.holdReasons.map(serverText).join(" · ") ?? ""}</span>
            {!!o.addLines?.length && <span className="inline-flex items-center gap-1 text-[12px] text-coral"><Gift className="size-3" />{o.addLines.map((l) => `${l.quantity}× ${l.sku}`).join(", ")}</span>}
            <span className="ml-auto text-[12px] text-fg-subtle truncate">{o.appliedRules.map((r) => r.name).join(", ")}{o.shippingMethodId ? ` → ${o.shippingMethodId}` : ""}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
